import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { XIcon } from '../components/icons';
import { Button, ErrorText, Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { timecode } from '../lib/format';
import type { ThumbnailCrop, ThumbnailResponse, VideoDetail } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchShare } from '../queries/share';
import { clearThumbnail, fetchVideo, setThumbnailFrame, uploadThumbnail } from '../queries/videos';

/**
 * The still a recording is shown by: in the library, on a pasted link, over
 * the player before it starts. The poster the transcoder made unless the
 * owner picks something else, either a moment of the recording or a picture
 * of their own cropped to the card.
 *
 * Its own dialog rather than a part of the timeline because the timeline is
 * the edit in progress, and a frame has to come from the version that plays
 * now: slate-api cuts it from the rendered MP4 at the second picked here, so
 * the scrubber plays that same MP4. An upload is sent whole with the part to
 * keep as fractions, and slate-api does the crop, so it comes out 1280x720
 * whichever way it was made. Saved when chosen, not with Finish: it is not
 * part of the edit and needs no render. A save closes the dialog and the
 * editor says so, because landing back on the first screen of the dialog
 * read as though nothing had happened.
 */

/** Every card and preview a thumbnail is shown in. */
const ASPECT = 16 / 9;
/** How far past filling the frame a picture can be zoomed. */
const MAX_ZOOM = 5;
/** What slate-api takes; a phone photo is well inside it. */
const MAX_UPLOAD_BYTES = 12 * 1024 * 1024;
const UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

type Mode = 'choose' | 'frame' | 'crop';

interface Picked {
  url: string;
  base64: string;
  contentType: string;
}

export function ThumbnailDialog({
  videoId,
  shareId,
  onClose,
  onSaved,
}: {
  videoId: string;
  shareId: string;
  onClose: () => void;
  /** After a save, with what the thumbnail is now; the dialog is closing. */
  onSaved: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('choose');
  const [picked, setPicked] = useState<Picked | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => () => (picked ? URL.revokeObjectURL(picked.url) : undefined), [picked]);

  const detail = useQuery({ queryKey: qk.video(videoId), queryFn: () => fetchVideo(videoId) });
  const share = useQuery({ queryKey: qk.share(shareId), queryFn: () => fetchShare(shareId) });
  const mp4Url = share.data?.status === 'ready' ? (share.data.playback?.mp4Url ?? null) : null;
  const posterUrl = detail.data?.posterUrl ?? share.data?.posterUrl ?? null;
  const custom = Boolean(detail.data?.thumbnailKey);

  const settle = (source: 'frame' | 'upload' | 'default') => (next: ThumbnailResponse) => {
    track('Thumbnail Changed', { video_id: shareId, source });
    queryClient.setQueryData<VideoDetail>(qk.video(videoId), (old) => (old ? { ...old, ...next } : old));
    void queryClient.invalidateQueries({ queryKey: qk.share(shareId) });
    void queryClient.invalidateQueries({ queryKey: ['videos'] });
    onSaved(source === 'default' ? 'Thumbnail back to the default' : 'Thumbnail saved');
    onClose();
  };
  const frame = useMutation({ mutationFn: (at: number) => setThumbnailFrame(videoId, at), onSuccess: settle('frame') });
  const upload = useMutation({
    mutationFn: (crop: ThumbnailCrop) => {
      if (!picked) throw new Error('No picture was picked.');
      return uploadThumbnail(videoId, { contentType: picked.contentType, data: picked.base64, crop });
    },
    onSuccess: settle('upload'),
  });
  const reset = useMutation({ mutationFn: () => clearThumbnail(videoId), onSuccess: settle('default') });

  const onFile = (file: File | undefined) => {
    setPickError(null);
    upload.reset();
    if (!file) return;
    if (!UPLOAD_TYPES.includes(file.type)) {
      setPickError('Use a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setPickError('Pictures are limited to 12 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const base64 = result.slice(result.indexOf(',') + 1);
      if (!base64) {
        setPickError('That image could not be read.');
        return;
      }
      setPicked({ url: URL.createObjectURL(file), base64, contentType: file.type });
      setMode('crop');
    };
    reader.onerror = () => setPickError('That image could not be read.');
    reader.readAsDataURL(file);
  };

  const busy = frame.isPending || upload.isPending || reset.isPending;
  const title = mode === 'frame' ? 'Pick a frame' : mode === 'crop' ? 'Crop the thumbnail' : 'Thumbnail';
  const error =
    pickError ??
    (frame.isError ? errorMessage(frame.error) : upload.isError ? errorMessage(upload.error) : reset.isError ? errorMessage(reset.error) : null);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/70 p-6 pt-20"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div role="dialog" aria-label={title} className="w-full max-w-lg rounded-lg border border-hairline bg-slate-900 p-4 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-h4 text-chalk">{title}</h2>
            <p className="mt-0.5 text-caption text-chalk-faint">
              {mode === 'frame'
                ? 'Drag to the moment you want. This is the recording as it plays now.'
                : mode === 'crop'
                  ? 'Drag to move it, scroll or use the slider to zoom, then save.'
                  : 'What the recording shows in the library and on a shared link.'}
            </p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={busy} className="text-chalk-dim hover:text-chalk">
            <XIcon size={16} />
          </button>
        </div>

        <div className="mt-4">
          {mode === 'frame' && mp4Url ? (
            <FramePicker url={mp4Url} durationSec={share.data?.durationSec ?? null} busy={frame.isPending} onCancel={() => setMode('choose')} onPick={(at) => frame.mutate(at)} />
          ) : mode === 'crop' && picked ? (
            <Cropper
              url={picked.url}
              busy={upload.isPending}
              onCancel={() => {
                setPicked(null);
                setMode('choose');
              }}
              onCrop={(crop) => upload.mutate(crop)}
            />
          ) : (
            <div className="space-y-3">
              <div className="relative aspect-video w-full overflow-hidden rounded border border-hairline bg-slate-950">
                {posterUrl ? <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
                {busy || detail.isPending ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/55">
                    <Spinner size={18} className="text-chalk-faint" />
                  </div>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" disabled={!mp4Url || busy} onClick={() => setMode('frame')}>
                  Pick a frame
                </Button>
                <Button size="sm" className="flex-1" disabled={busy} onClick={() => fileInput.current?.click()}>
                  Upload an image
                </Button>
                <input
                  ref={fileInput}
                  type="file"
                  accept={UPLOAD_TYPES.join(',')}
                  className="hidden"
                  onChange={(event) => {
                    onFile(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
              </div>
              {!mp4Url && share.data ? (
                <p className="text-caption text-chalk-faint">A frame can be picked once the recording has processed.</p>
              ) : null}
              {custom ? (
                <button
                  type="button"
                  onClick={() => reset.mutate()}
                  disabled={busy}
                  className="text-caption text-chalk-dim underline-offset-2 hover:text-chalk hover:underline disabled:opacity-40"
                >
                  Use the default
                </button>
              ) : null}
            </div>
          )}
          {error ? (
            <div className="mt-3">
              <ErrorText>{error}</ErrorText>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The rendered recording, paused, with a bar to drag through it. What is on
 * screen when "Use this frame" is pressed is the frame.
 */
function FramePicker({
  url,
  durationSec,
  busy,
  onCancel,
  onPick,
}: {
  url: string;
  durationSec: number | null;
  busy: boolean;
  onCancel: () => void;
  onPick: (at: number) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(durationSec ?? 0);

  return (
    <div className="space-y-3">
      <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded border border-hairline bg-black" data-theme="dark">
        <video
          ref={video}
          src={url}
          muted
          playsInline
          preload="auto"
          className="max-h-full max-w-full"
          onLoadedMetadata={(event) => {
            const duration = event.currentTarget.duration;
            if (Number.isFinite(duration) && duration > 0) setLength(duration);
          }}
          onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
        />
      </div>
      <input
        type="range"
        min={0}
        max={length || 0}
        step={0.04}
        value={Math.min(time, length || 0)}
        disabled={length <= 0 || busy}
        aria-label="Moment of the recording"
        onChange={(event) => {
          const at = Number(event.target.value);
          setTime(at);
          if (video.current) video.current.currentTime = at;
        }}
        className="w-full accent-chalk"
      />
      <div className="flex items-center gap-3">
        <span className="font-mono tnum text-caption text-chalk-dim">
          {timecode(time)} / {timecode(length)}
        </span>
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Back
        </Button>
        <Button size="sm" variant="primary" busy={busy} disabled={length <= 0} onClick={() => onPick(video.current?.currentTime ?? time)}>
          Save this frame
        </Button>
      </div>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * A 16:9 window over the picture: drag to move, scroll or slide to zoom. The
 * picture always covers the window, so there is never an edge of nothing in
 * the crop. What is sent is where the window sits, as fractions of the
 * picture as the browser drew it, which is the picture turned upright.
 */
function Cropper({ url, busy, onCancel, onCrop }: { url: string; busy: boolean; onCancel: () => void; onCrop: (crop: ThumbnailCrop) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState({ width: 0, height: 0 });
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setFrame({ width: el.clientWidth, height: el.clientWidth / ASPECT });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Covering the frame at zoom 1.
  const fit = natural && frame.width > 0 ? Math.max(frame.width / natural.width, frame.height / natural.height) : 1;
  const baseWidth = natural ? natural.width * fit : frame.width;
  const baseHeight = natural ? natural.height * fit : frame.height;
  const within = (next: { x: number; y: number }, scale: number) => {
    const slackX = (baseWidth * scale - frame.width) / 2;
    const slackY = (baseHeight * scale - frame.height) / 2;
    return { x: clamp(next.x, -slackX, slackX), y: clamp(next.y, -slackY, slackY) };
  };
  const zoomTo = (next: number) => {
    const scale = clamp(next, 1, MAX_ZOOM);
    setZoom(scale);
    setOffset((was) => within(was, scale));
  };

  // A wheel over the frame zooms rather than scrolls the page; React's own
  // wheel listener is passive and cannot stop that.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      zoomTo(zoom * Math.exp(-event.deltaY * 0.002));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!natural) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerX: event.clientX, pointerY: event.clientY, x: offset.x, y: offset.y };
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = drag.current;
    if (!start) return;
    setOffset(within({ x: start.x + event.clientX - start.pointerX, y: start.y + event.clientY - start.pointerY }, zoom));
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const crop = (): ThumbnailCrop => {
    const shownWidth = baseWidth * zoom;
    const shownHeight = baseHeight * zoom;
    const width = Math.min(1, frame.width / shownWidth);
    const height = Math.min(1, frame.height / shownHeight);
    return {
      x: clamp(((shownWidth - frame.width) / 2 - offset.x) / shownWidth, 0, 1 - width),
      y: clamp(((shownHeight - frame.height) / 2 - offset.y) / shownHeight, 0, 1 - height),
      width,
      height,
    };
  };

  return (
    <div className="space-y-3">
      <div
        ref={box}
        className={cx(
          'relative w-full touch-none select-none overflow-hidden rounded border border-hairline bg-black',
          natural ? 'cursor-grab active:cursor-grabbing' : '',
        )}
        style={{ aspectRatio: `${ASPECT}` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          src={url}
          alt=""
          draggable={false}
          onLoad={(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
          className={cx('pointer-events-none absolute max-w-none', natural ? 'opacity-100' : 'opacity-0')}
          style={{
            width: baseWidth,
            height: baseHeight,
            left: (frame.width - baseWidth) / 2,
            top: (frame.height - baseHeight) / 2,
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          }}
        />
        {!natural ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner size={18} className="text-chalk-faint" />
          </div>
        ) : null}
      </div>
      <input
        type="range"
        min={1}
        max={MAX_ZOOM}
        step={0.01}
        value={zoom}
        disabled={!natural || busy}
        aria-label="Zoom"
        onChange={(event) => zoomTo(Number(event.target.value))}
        className="w-full accent-chalk"
      />
      <div className="flex items-center justify-end gap-3">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Back
        </Button>
        <Button size="sm" variant="primary" busy={busy} disabled={!natural} onClick={() => onCrop(crop())}>
          Save thumbnail
        </Button>
      </div>
    </div>
  );
}
